import React from 'react';
import { useState } from 'react';
import { Box, Card, Chip, Grid, IconButton, LinearProgress, Typography } from '@mui/material';
import { BsThreeDotsVertical } from 'react-icons/bs';
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import TitleBar from 'components/TitleBar';
import RiskListPopUp from './riskOverviewListPopUp';
import StatusBadge from 'components/badge/StatusBadge';
import RiskSeverityBadge from "components/badge/RiskSeverityBadge";
import moment from 'moment';
import { enterpriseTokens, overviewChartPalette } from 'styles/theme';

const RiskOverview = ({ data, riskRatingData }) => {

    const [openPopup, setOpenPopup] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState("");

    const handleChartClick = (statusName) => {
        const status = getStatus(statusName)
        setSelectedStatus(status);
        setOpenPopup(true);
    };

    const getStatus = (status) => {
        switch (status) {
            case "Active Exposure":
                return "Active";
            case "Resolved Exposure":
                return "Completed"
            default:
                return "";
        }
    };



    const riskData = [
        { name: "Active Exposure", percentage: data?.data?.openPercentage ?? 0, color: overviewChartPalette.coral },
        { name: "Resolved Exposure", percentage: data?.data?.closedPercentage ?? 0, color: overviewChartPalette.teal },
    ];

     const columns = [
            {
                name: "Title",
                field: "title",
                width: "360px",
                sticky: true,
               
            },
            {
                name: "Category",
                field: "category",
                sticky: true,
                
            },
            {
                name: 'Status',
                field: 'riskStatus',
                renderer: (row) => (
                    <StatusBadge status={row?.riskStatus} />
                )
            },
            {
                name: "Likelihood",
                field: "likelihood",
            },
            {
                name: "Impact",
                field: "impact",
            },
            {
                name: "Risk Rating",
                field: "riskRating",
            },
            {
                name: "Severity",
                field: "severity",
                renderer: (row) => (
                    <RiskSeverityBadge status={row?.severity} />
                )
            },
            {
                name: "Mitigation Action Status",
                field: "mitigationActionStatus",
                
            },
            {
                name: "Target Closure Date",
                field: "targetClosureDate",
                renderer: (row) => (
                    row?.targetClosureDate ? moment.utc(row.targetClosureDate).format("DD MMM YYYY") : "N/A"
                ),
            },
            {
                name: "Entity/Person Responsible",
                field: "entityPersonResponsible",
            },
        ];

    const options = {
        chart: {
            type: "pie",
            backgroundColor: "transparent",
            height: "340px",


            events: {
                render: function () {
                    // const chart = this;

                    // // Remove old text
                    // if (chart.customCenterText) chart.customCenterText.destroy();
                    // if (chart.customSubText) chart.customSubText.destroy();

                    // const centerX = chart.plotLeft + chart.plotWidth / 2;
                    // const centerY = chart.plotTop + chart.plotHeight / 2;

                    // // Main Center Number
                    // chart.customCenterText = chart.renderer
                    //     .text(`${totalRisk}`, centerX, centerY - 6)
                    //     .css({
                    //         fontSize: "24px",
                    //         fontWeight: 600,
                    //         fontFamily: "Outfit, sans-serif",
                    //         color: "#000",
                    //         textAnchor: "middle",
                    //     })
                    //     .attr({ align: "center" })
                    //     .add();

                    // // Subtext
                    // chart.customSubText = chart.renderer
                    //     .text("Total Risk", centerX, centerY + 18)
                    //     .css({
                    //         fontSize: "13px",
                    //         fontFamily: "Outfit, sans-serif",
                    //         color: "#737791",
                    //         textAnchor: "middle",
                    //     })
                    //     .attr({ align: "center" })
                    //     .add();
                },
            },
        },

        title: { text: null },

        plotOptions: {
            pie: {
                innerSize: "76%",
                dataLabels: { enabled: false },
                borderRadius: 0,
                borderWidth: 3,
                borderColor: "#fff",
                size: "100%",
                cursor: "pointer",

                 point: {
                events: {
                    click: function () {
                        // this.name = clicked slice name
                        handleChartClick(this.name);
                    }
                }
            }
            },
            
        },

        series: [
            {
                data: riskData.map((item) => ({
                    name: item.name,
                    y: item.percentage,
                    color: item.color,
                })),
            },
        ],

        tooltip: {
            useHTML: true,
            formatter: function () {
                // const total = totalRisk;
                const percent = this.y;
                // const calcValue = Math.floor((percent / 100) * total);

                return `
                    <div style="padding:6px 8px;">
                        <b>${this.point.name}</b><br/>
                        Percentage: ${percent}%
                    </div>
                `;
            },
        },
        credits: { enabled: false },
    };

    return (
        <>
            <Card
                elevation={0}
                sx={{
                    borderRadius: "3px",
                    border: `1px solid ${enterpriseTokens.lineSoft}`,
                    p: 0,
                    height: '100%',
                    //pb: 2
                }}
            >
                <Grid container spacing={0} >
                    <TitleBar title={"Risk Overview"} description={"Quick snapshot of overall risk resolution"} />
                </Grid>
                <Grid container spacing={3} sx={{ p: 2, pb: 0 }}>
                    <Grid size={{ md: 5 }} sx={{ pt: 3 }}>
                        <Box>
                            <Typography sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontWeight: 400,
                                fontSize: '25px',
                                lineHeight: "100%",
                                letterSpacing: "0px",
                                color: overviewChartPalette.coral
                            }}>Critical Exposure</Typography>
                            <Typography sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontWeight: 500,
                                fontSize: '36px',
                                lineHeight: "100%",
                                letterSpacing: "0px",
                                color: overviewChartPalette.coral,
                                mt: "3px"
                            }}>{data?.data?.totalHighRisks ?? 0}</Typography>
                        </Box>
                        <Box sx={{ display: "grid", gap: "15px", mt: 2.5 }}>
                            {riskData?.map((item) => (
                                <Box key={item.name} sx={{ display: "flex", gap: 1, alignItems: "center", border: "1px solid #EAEAEA", p: 1.5 }}>
                                    <Box sx={{ width: "18px", height: "18px", borderRadius: "50%", bgcolor: `${item.color}` }}></Box>
                                    <Box>
                                        <Typography sx={{
                                            fontFamily: "Outfit, sans-serif",
                                            fontWeight: 400,
                                            fontSize: '16px',
                                            lineHeight: "100%",
                                            letterSpacing: "0px",
                                            color: '#010814',
                                        }}>{item.name}: {item.percentage}%</Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                        <Box sx={{
                            border: "1px solid #E5E5E5",
                            p: 2,
                            // ml: 2,
                            mt: "30px"
                        }}>
                            <Typography sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontWeight: 600,
                                fontSize: '24px',
                                letterSpacing: "0px",
                                color: '#7D7F81'
                            }}> {riskRatingData?.data?.averageRiskRating ?? 0} <Typography sx={{ fontSize: "14px" }} component={"span"}> Exposure Score</Typography></Typography>
                            {/* <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontWeight: 600,
                                fontSize: '24px',
                                letterSpacing: "0px",
                                color: '#34C759'
                            }}>
                                {2}
                                <Typography sx={{ fontSize: "14px" }} component={"span"}>
                                    Completed
                                </Typography>
                            </Typography>
                        </Box> */}
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 7 }} >
                        <Box>
                            <HighchartsReact highcharts={Highcharts} options={options} />
                        </Box>
                    </Grid>
                </Grid>
            </Card>
            {
                openPopup &&
                <RiskListPopUp
                    open={openPopup}
                    handleClose={() => setOpenPopup(false)}
                    data={selectedStatus}
                    columns={columns}
                   
                />
            }
        </>
    )
}

export default RiskOverview
